import { decodeJwt, getCookie, isJwtExpired } from "@/utils/cookieUtils";
import React, { createContext, useState, useContext, useEffect } from "react";
import { TClient } from "@/types/types";
import { toast } from "sonner";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { Button } from "@/components/ui/button";
import { handleMetamaskSignIn } from "@/services/auth";
import { handleGoogleSignIn } from "@/services/auth";
import { GoogleLogin } from "@react-oauth/google";
import MMLogo from "@/assets/metamask-icon.webp";
import ChLogo from "@/assets/logo.svg";
import { checkInviteCode } from "@/services/investor";

interface UserContextType {
  userInfo: TClient;
  hidePortfolio: boolean;
  setUserInfo: (userInfo: TClient) => void;
  setHidePortfolio: (value: boolean) => void;
  setCurrency: (currency: "USD" | "INR") => void;
  currency: "USD" | "INR";
  exchangeRate: number | null;
  fetchExchangeRate: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [userInfo, setUserInfo] = useState<TClient>({
    id: 0,
    name: "",
    email: "",
    address: "",
    has_wallet: false,
    waitlisted: true,
  });
  const [inviteCode, setInviteCode] = useState("");
  useEffect(() => {
    const token = getCookie("jwt");
    if (token && !isJwtExpired(token)) {
      const savedUserInfo = decodeJwt(token) as TClient | null;
      if (savedUserInfo) {
        setUserInfo(savedUserInfo);
      }
    }
  }, []);
  const [hidePortfolio, setHidePortfolio] = useState(false);
  const [currency, setCurrency] = useState<"USD" | "INR">("USD"); 
  const updateUserInfo = (newUserInfo: TClient) => {
    setUserInfo(newUserInfo);
  };

  const handleInviteCode = async (code: string) => {
    const response = await checkInviteCode(code);
    if (response.token) {
      setUserInfo({ ...userInfo, waitlisted: false });
      updateUserInfo({ ...userInfo, waitlisted: false });
    } else {
      toast.error("Invalid invite code. Please try again.");
    }
  };
  const [exchangeRate, setExchangeRate] = useState<number | null>(null); 

  const fetchExchangeRate = async () => {
    try {
      const res = await fetch(
        "https://v6.exchangerate-api.com/v6/a972ea59813490d8fa139278/latest/USD"
      );
      const data = await res.json();
      
      if (data.result === "success" && data.conversion_rates?.INR) {
        setExchangeRate(data.conversion_rates.INR); // Uncomment if storing in state
        // console.log(exchangeRate);
      } else {
        console.error("Invalid response format:", data);
      }
    } catch (error) {
      console.error("Error fetching exchange rate:", error);
    }
  };
  

  return (
    <UserContext.Provider value={{hidePortfolio,fetchExchangeRate,exchangeRate, setHidePortfolio,currency, setCurrency, userInfo, setUserInfo: updateUserInfo }}>
      {!userInfo || userInfo.id === 0 ? (
        // Step 1: Show sign-in options
        <div style={{ position: "relative" }}>
          <div style={{ filter: "blur(5px)", pointerEvents: "none" }}>
            {children}
          </div>
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 1,
            }}
          >
            <div className="flex justify-center items-center h-screen">
              <div className="bg-black p-12 rounded-xl shadow-lg w-96">
                <img
                  src={ChLogo}
                  alt="ChLogo"
                  className="h-16 w-16 mb-4 mx-auto"
                />
                <h2 className="text-xl font-bold text-white text-center mb-4">
                  Sign in to Continue
                </h2>
                <div className="flex items-center gap-4 justify-center">
                  <img
                    onClick={() => handleMetamaskSignIn(setUserInfo)}
                    src={MMLogo}
                    alt="MetaMask"
                    className="h-8 w-8 cursor-pointer"
                  />
                  <span className="text-muted-foreground">or</span>
                  <GoogleLogin
                    onSuccess={(credentialResponse) =>
                      handleGoogleSignIn(credentialResponse, setUserInfo)
                    }
                    type="icon"
                    theme="filled_black"
                    size="large"
                    text="continue_with"
                    shape="circle"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : userInfo.waitlisted ? (
        // Step 2: Show invite code modal
        <div style={{ position: "relative" }}>
          <div style={{ filter: "blur(5px)", pointerEvents: "none" }}>
            {children}
          </div>
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 1,
            }}
          >
            <div className="modal bg-opacity-90 bg-black p-6 rounded-lg shadow-lg">
              <h2 className="text-m text-center text-gray mb-4">
                Wohoo. You're on the Waitlist!
              </h2>
              <h2 className="text-xl text-center font-bold text-white mb-4">
                Enter Your Invite Code
              </h2>
              <InputOTP
                maxLength={6}
                minLength={6}
                value={inviteCode}
                onChange={(value) => setInviteCode(value.toUpperCase())}
                className="mb-4"
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                </InputOTPGroup>
                <InputOTPSeparator />
                <InputOTPGroup>
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
              <Button
                className="mt-4 w-full bg-black hover:bg-gray-800 text-white font-semibold py-2 rounded-md transition duration-200"
                onClick={async () => {
                  await handleInviteCode(inviteCode);
                }}
              >
                Submit
              </Button>
            </div>
          </div>
        </div>
      ) : (
        // Step 3: Show content if user is not on the waitlist
        children
      )}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
